// Safety hold (2026-10-26, backend llm/safety_hold): after the athlete reports medical red flags in the chat, plan
// changes are held by the SERVER until an explicit, recorded medical release — never a chat message. These are thin
// clients for the backend's hold API; nothing here calls an AI service.
import { useCallback, useEffect, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';

// The backend's 400 reasons -> what the athlete reads (never a raw code).
export const RELEASE_ERRORS = {
  attestation_required: 'כדי לשחרר, סמן שנבדקת וקיבלת אישור רפואי לחזור לפעילות.',
  cleared_on_required: 'בחר את התאריך שבו קיבלת את האישור הרפואי.',
  cleared_before_event: 'תאריך האישור לא יכול להיות לפני הדיווח על התסמינים.',
  cleared_in_future: 'תאריך האישור לא יכול להיות בעתיד.',
};
export const RELEASE_FAILED = 'לא הצלחתי לשמור את האישור כרגע. נסה שוב בעוד רגע.';

export async function fetchSafetyHold(fetcher, uid) {
  const res = await fetcher(`${API_BASE_URL}/coach/safety-hold?user_id=${encodeURIComponent(uid)}`);
  if (!res || !res.ok) return null;                 // unknown -> keep what is shown (never "released" by an error)
  return res.json();
}

/** {ok: true, message} | {ok: false, error} — error is always readable Hebrew. */
export async function releaseSafetyHold(fetcher, uid, { attest, clearedOn }) {
  let res;
  try {
    res = await fetcher(`${API_BASE_URL}/coach/safety-hold/release?user_id=${encodeURIComponent(uid)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attest: attest === true, cleared_on: clearedOn || null }),
    });
  } catch {
    return { ok: false, error: RELEASE_FAILED };
  }
  if (res && res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: true, message: data.message || '' };
  }
  let code = null;
  try { code = ((await res.json()) || {}).detail?.error || null; } catch { code = null; }
  return { ok: false, error: RELEASE_ERRORS[code] || RELEASE_FAILED };
}

/** The hold shown in the chat: loaded on mount (a page refresh shows it again), updated from chat replies
 *  (setFromReply), released only through the server. */
export function useSafetyHold(fetcher, uid) {
  const [hold, setHold] = useState(null);           // null = none / unknown; {active:true, kind, since}
  useEffect(() => {
    if (!uid) return undefined;
    let live = true;
    fetchSafetyHold(fetcher, uid)
      .then((h) => { if (live && h) setHold(h.active ? h : null); })
      .catch(() => {});
    return () => { live = false; };
  }, [fetcher, uid]);
  const setFromReply = useCallback((h) => {
    if (h && h.active) setHold(h);
  }, []);
  const release = useCallback(async (form) => {
    const out = await releaseSafetyHold(fetcher, uid, form);
    if (out.ok) setHold(null);
    return out;
  }, [fetcher, uid]);
  return { hold, setFromReply, release };
}
